import {Directive, EmbeddedViewRef, Input, OnInit, TemplateRef, ViewContainerRef, ɵstringify as stringify} from '@angular/core';
import {NgIfContext} from '@angular/common';
import {ExternalReferenceService} from '../services/external-reference.service';

/**
 * EXTERNAL REFERENCE STRUCTURAL DIRECTIVE
 *
 * Creates or removes a DOM element depending on isActive() from ExternalReferenceService
 *
 * The value is constant for the whole session (cached by ExternalReferenceService),
 * so it is evaluated once in the main @Input setter — same approach as SecureDirective.
 *
 * USAGE
 *  - Render when mode is active:
 *      <ng-container *siExternalReference>…</ng-container>
 *
 *  - Render when mode is NOT active (invert):
 *      <ng-container *siExternalReference="false">…</ng-container>
 *
 *  - With else template:
 *      <ng-container *siExternalReference; else noIframe">in iframe</ng-container>
 *      <ng-template #noIframe>not in iframe</ng-template>
 *
 *  - Inverted with else:
 *      <ng-container *siExternalReference="false; else inIframe">not in iframe</ng-container>
 *      <ng-template #inIframe>in iframe</ng-template>
 */
@Directive({
  selector: '[siExternalReference]'
})
export class ExternalReferenceDirective implements OnInit {
  private _context: NgIfContext<boolean> = new NgIfContext<boolean>();
  private _thenTemplateRef: TemplateRef<NgIfContext<boolean>> | null = null;
  private _elseTemplateRef: TemplateRef<NgIfContext<boolean>> | null = null;
  private _thenViewRef: EmbeddedViewRef<NgIfContext<boolean>> | null = null;
  private _elseViewRef: EmbeddedViewRef<NgIfContext<boolean>> | null = null;
  renderWhenActive: boolean;

  constructor(
    private externalReferenceService: ExternalReferenceService,
    private templateRef: TemplateRef<NgIfContext<boolean>>,
    private viewContainer: ViewContainerRef
  ) {
    this._thenTemplateRef = templateRef;
  }

  // Bare usage (*siExternalReference without value): setter is not called, so ngOnInit handles it as render-when-active.
  ngOnInit(): void {
    if (this.renderWhenActive === undefined) {
      this.renderWhenActive = true;
      this._context.ngIf = this.renderWhenActive === this.externalReferenceService.isActive();
      this._context.$implicit = this._context.ngIf;
      this._updateView();
    }
  }

  // *siExternalReference="false" → renders when external reference is NOT active (e.g. top-bar, side-menu).
  // *siExternalReference or *siExternalReference="true" → renders only when external reference IS active.
  @Input()
  set siExternalReference(value: boolean) {
    this.renderWhenActive = value !== false;
    this._context.ngIf = this.renderWhenActive === this.externalReferenceService.isActive();
    this._context.$implicit = this._context.ngIf;
    this._updateView();
  }

  /**
   * A template to show when the condition is met.
   */
  @Input()
  set siExternalReferenceThen(templateRef: TemplateRef<NgIfContext<boolean>> | null) {
    this.assertTemplate('siExternalReferenceThen', templateRef);
    this._thenTemplateRef = templateRef;
    this._thenViewRef = null;
    this._updateView();
  }

  /**
   * A template to show when the condition is NOT met.
   */
  @Input()
  set siExternalReferenceElse(templateRef: TemplateRef<NgIfContext<boolean>> | null) {
    this.assertTemplate('siExternalReferenceElse', templateRef);
    this._elseTemplateRef = templateRef;
    this._elseViewRef = null;
    this._updateView();
  }

  assertTemplate(property: string, templateRef: TemplateRef<any> | null): void {
    const isTemplateRefOrNull = !!(!templateRef || templateRef.createEmbeddedView);
    if (!isTemplateRefOrNull) {
      throw new Error(`${property} must be a TemplateRef, but received '${stringify(templateRef)}'.`);
    }
  }

  private _updateView(): void {
    if (this._context.$implicit) {
      if (!this._thenViewRef) {
        this.viewContainer.clear();
        this._elseViewRef = null;
        if (this._thenTemplateRef) {
          this._thenViewRef = this.viewContainer.createEmbeddedView(this._thenTemplateRef, this._context);
        }
      }
    } else {
      if (!this._elseViewRef) {
        this.viewContainer.clear();
        this._thenViewRef = null;
        if (this._elseTemplateRef) {
          this._elseViewRef = this.viewContainer.createEmbeddedView(this._elseTemplateRef, this._context);
        }
      }
    }
  }
}
